/**
 * Insurance Lead Detail Component
 * Modal/slideout showing full lead details with action buttons
 */

import React, { useState, useEffect } from 'react';
import { InsuranceLead, ManualEntryData } from '../../types/insurance';
import { LeadStatusBadge } from './LeadStatusBadge';
import { MatchConfidenceBadge } from './MatchConfidenceBadge';

interface InsuranceLeadDetailProps {
  lead: InsuranceLead;
  isOpen: boolean;
  onClose: () => void;
  onClaim: (leadId: string) => Promise<void>;
  onUnclaim: (leadId: string) => Promise<void>;
  onComplete: (leadId: string, outcome: 'quoted_purchased' | 'quoted_not_purchased' | 'bad_lead', notes?: string) => Promise<void>;
  onUpdateManualEntry: (leadId: string, data: ManualEntryData) => Promise<void>;
  isCurrentUser: (userId?: string) => boolean;
  autoOpenComplete?: boolean;
}

export const InsuranceLeadDetail: React.FC<InsuranceLeadDetailProps> = ({
  lead,
  isOpen,
  onClose,
  onClaim,
  onUnclaim,
  onComplete,
  onUpdateManualEntry,
  isCurrentUser,
  autoOpenComplete = false,
}) => {
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-open completion modal if requested
  useEffect(() => {
    if (autoOpenComplete && isOpen) {
      setShowCompleteModal(true);
    }
  }, [autoOpenComplete, isOpen]);

  const isClaimedByMe = lead.claimedBy && isCurrentUser(lead.claimedBy);
  const canClaim = lead.status === 'unclaimed' || lead.status === 'needs_manual_review';
  const canUnclaim = lead.status === 'claimed' && isClaimedByMe;
  const canComplete = lead.status === 'claimed' && isClaimedByMe;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleAction = async (action: () => Promise<void>) => {
    setIsLoading(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      setError(err.message || 'Action failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Shared content component to avoid duplication
  const LeadContent = ({ forPrint = false }: { forPrint?: boolean }) => (
    <>
      {/* Print Header - only in print version */}
      {forPrint && (
        <div className="mb-6 pb-4 border-b-2 border-gray-300">
          <h1 className="text-2xl font-bold text-gray-900">Insurance Lead Details</h1>
          <p className="text-sm text-gray-500 mt-1">
            Printed on {new Date().toLocaleDateString('en-US', {
              month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
            })}
          </p>
        </div>
      )}

      {/* Customer Info */}
      <section className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {lead.sourceData.customerName}
        </h2>
        <div className="text-sm text-gray-600 space-y-1">
          <p><span className="font-medium">Location:</span> {lead.locationName}</p>
          <p><span className="font-medium">Stock #:</span> {lead.stockNumber}</p>
          <p><span className="font-medium">Sold:</span> {formatDate(lead.sourceData.soldDatetime)}</p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
          Contact Information
        </h3>
        <div className={`${forPrint ? 'border border-gray-300' : 'bg-gray-50'} rounded-lg p-4 space-y-2 text-sm`}>
          <p><span className="font-medium">Primary Phone:</span> {lead.sourceData.primaryPhone}</p>
          {lead.sourceData.cellPhone && lead.sourceData.cellPhone !== lead.sourceData.primaryPhone && (
            <p><span className="font-medium">Cell:</span> {lead.sourceData.cellPhone}</p>
          )}
          {lead.sourceData.email && (
            <p><span className="font-medium">Email:</span> {lead.sourceData.email}</p>
          )}
          {lead.sourceData.address && (
            <p>
              <span className="font-medium">Address:</span>{' '}
              {[lead.sourceData.address, lead.sourceData.city, lead.sourceData.state, lead.sourceData.postalCode]
                .filter(Boolean)
                .join(', ')}
            </p>
          )}
        </div>
      </section>

      {/* DealerBuilt Matched Data */}
      {lead.matchedData && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            DealerBuilt Record
          </h3>
          <div className={`${forPrint ? 'border border-gray-300' : 'bg-green-50'} rounded-lg p-4 space-y-2 text-sm`}>
            <p><span className="font-medium">Deal #:</span> {lead.matchedData.dealNumber}</p>
            {lead.matchedData.salesperson && (
              <p><span className="font-medium">Salesperson:</span> {lead.matchedData.salesperson}</p>
            )}
            {lead.matchedData.vehicleYear && (
              <p>
                <span className="font-medium">Vehicle:</span>{' '}
                {[lead.matchedData.vehicleYear, lead.matchedData.vehicleMake, lead.matchedData.vehicleModel]
                  .filter(Boolean)
                  .join(' ')}
              </p>
            )}
            {lead.matchedData.vin && (
              <p><span className="font-medium">VIN:</span> {lead.matchedData.vin}</p>
            )}
            {/* Driver's License */}
            {lead.matchedData.driversLicense ? (
              <p>
                <span className="font-medium">Driver's License:</span> {lead.matchedData.driversLicense}
                {lead.matchedData.dlState && ` (${lead.matchedData.dlState})`}
              </p>
            ) : (
              <p className="text-gray-400 italic">
                <span className="font-medium">Driver's License:</span> Not on file
              </p>
            )}
            {/* Current Insurance */}
            {lead.matchedData.insuranceCompany ? (
              <p>
                <span className="font-medium">Current Insurance:</span> {lead.matchedData.insuranceCompany}
                {lead.matchedData.policyNumber && ` - ${lead.matchedData.policyNumber}`}
              </p>
            ) : (
              <p className="text-gray-400 italic">
                <span className="font-medium">Current Insurance:</span> Not on file
              </p>
            )}
            {/* Current Agent */}
            {lead.matchedData.insuranceAgent ? (
              <p>
                <span className="font-medium">Current Agent:</span> {lead.matchedData.insuranceAgent}
              </p>
            ) : (
              <p className="text-gray-400 italic">
                <span className="font-medium">Current Agent:</span> Not on file
              </p>
            )}
            {/* Policy Expiration Date */}
            {lead.matchedData.policyExpirationDate ? (
              <p>
                <span className="font-medium">Policy Expires:</span>{' '}
                {new Date(lead.matchedData.policyExpirationDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            ) : (
              <p className="text-gray-400 italic">
                <span className="font-medium">Policy Expires:</span> Not on file
              </p>
            )}
            {/* Financing / Lender Info */}
            <div className="border-t border-gray-200 mt-3 pt-3">
              <p className="font-medium text-gray-700 mb-1">Financing</p>
              {lead.matchedData.lenderName ? (
                <>
                  <p>
                    <span className="font-medium">Lender:</span> {lead.matchedData.lenderName}
                    {lead.matchedData.lenderCode && ` (${lead.matchedData.lenderCode})`}
                  </p>
                  {lead.matchedData.lenderAddress && (
                    <p>
                      <span className="font-medium">Address:</span> {lead.matchedData.lenderAddress}
                      {lead.matchedData.lenderCsz && `, ${lead.matchedData.lenderCsz}`}
                    </p>
                  )}
                  {lead.matchedData.secondLienHolder && (
                    <p className="mt-1">
                      <span className="font-medium">2nd Lien Holder:</span> {lead.matchedData.secondLienHolder}
                      {lead.matchedData.secondLienHolderAddress && (
                        <span>
                          {' '}— {lead.matchedData.secondLienHolderAddress}
                          {lead.matchedData.secondLienHolderCsz && `, ${lead.matchedData.secondLienHolderCsz}`}
                        </span>
                      )}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-gray-400 italic">No lender on file</p>
              )}
            </div>
          </div>
          {lead.matchDetails && (
            <div className="mt-2 text-xs text-gray-500">
              {lead.matchDetails.matchedFields.length > 0 && (
                <p>Matched: {lead.matchDetails.matchedFields.join(', ')}</p>
              )}
              {lead.matchDetails.mismatchedFields.length > 0 && (
                <p className="text-yellow-600">
                  Mismatched: {lead.matchDetails.mismatchedFields.join(', ')}
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {/* Manual Entry (for needs_manual_review) */}
      {(lead.status === 'needs_manual_review' || lead.manualEntry) && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Manual Entry Data
          </h3>
          <div className={`${forPrint ? 'border border-gray-300' : 'bg-yellow-50'} rounded-lg p-4 text-sm`}>
            {lead.manualEntry ? (
              <div className="space-y-1">
                {lead.manualEntry.vehicleYear && (
                  <p>
                    <span className="font-medium">Vehicle:</span>{' '}
                    {[lead.manualEntry.vehicleYear, lead.manualEntry.vehicleMake, lead.manualEntry.vehicleModel]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                )}
                {lead.manualEntry.vin && (
                  <p><span className="font-medium">VIN:</span> {lead.manualEntry.vin}</p>
                )}
                {lead.manualEntry.driversLicense && (
                  <p><span className="font-medium">DL:</span> {lead.manualEntry.driversLicense}</p>
                )}
              </div>
            ) : (
              <p className="text-yellow-700">
                No DealerBuilt match found. Manual data entry required.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Claim Info */}
      {lead.claimedBy && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Assignment
          </h3>
          <div className={`${forPrint ? 'border border-gray-300' : 'bg-purple-50'} rounded-lg p-4 text-sm`}>
            <p>
              <span className="font-medium">Claimed by:</span>{' '}
              {isClaimedByMe ? 'You' : lead.claimedByName || 'Unknown'}
            </p>
            {lead.claimedAt && (
              <p><span className="font-medium">Claimed at:</span> {formatDate(lead.claimedAt)}</p>
            )}
          </div>
        </section>
      )}

      {/* Completion Info */}
      {lead.status === 'completed' && lead.completedAt && (
        <section className="mb-6">
          <div className={`${forPrint ? 'border border-gray-300' : 'bg-green-50'} rounded-lg p-4 text-sm text-green-700`}>
            <p className="font-medium">Completed at {formatDate(lead.completedAt)}</p>
          </div>
        </section>
      )}

      {/* Discard Info */}
      {lead.status === 'discarded' && (
        <section className="mb-6">
          <div className={`${forPrint ? 'border border-gray-300' : 'bg-gray-100'} rounded-lg p-4 text-sm text-gray-600`}>
            <p className="font-medium">
              Discarded by {lead.discardedByName || 'Unknown'}
              {lead.discardedAt && ` at ${formatDate(lead.discardedAt)}`}
            </p>
            {lead.discardReason && (
              <p className="mt-1">Reason: {lead.discardReason}</p>
            )}
          </div>
        </section>
      )}
    </>
  );

  return (
    <>
      {/* Print-only version - hidden on screen, shown when printing */}
      <div id="print-lead-detail" className="hidden print:block p-8">
        <LeadContent forPrint={true} />
      </div>

      {/* Screen version - shown on screen, hidden when printing */}
      <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Centered Modal */}
        <div className="relative max-w-2xl w-full max-h-[90vh] flex">
          <div className="relative w-full bg-white shadow-xl rounded-lg flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between rounded-t-lg">
              <div className="flex items-center gap-3">
                <LeadStatusBadge status={lead.status} />
                <MatchConfidenceBadge confidence={lead.matchConfidence} />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    document.body.classList.add('printing-lead-detail');
                    window.print();
                    document.body.classList.remove('printing-lead-detail');
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                  title="Print lead details"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              <LeadContent forPrint={false} />

              {/* Completion Modal */}
              {showCompleteModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-black bg-opacity-50"
                    onClick={() => setShowCompleteModal(false)}
                  />
                  <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Complete Lead
                    </h3>
                    <p className="text-gray-600 mb-6">
                      What was the outcome of this lead?
                    </p>
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => {
                          setShowCompleteModal(false);
                          handleAction(() => onComplete(lead.leadId, 'quoted_purchased'));
                        }}
                        disabled={isLoading}
                        className="w-full px-4 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md"
                      >
                        {isLoading ? 'Saving...' : 'Provided Quote - Purchased'}
                      </button>
                      <button
                        onClick={() => {
                          setShowCompleteModal(false);
                          handleAction(() => onComplete(lead.leadId, 'quoted_not_purchased'));
                        }}
                        disabled={isLoading}
                        className="w-full px-4 py-3 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 rounded-md"
                      >
                        {isLoading ? 'Saving...' : 'Provided Quote - Did Not Purchase'}
                      </button>
                      <button
                        onClick={() => {
                          setShowCompleteModal(false);
                          handleAction(() => onComplete(lead.leadId, 'bad_lead'));
                        }}
                        disabled={isLoading}
                        className="w-full px-4 py-3 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 rounded-md"
                      >
                        {isLoading ? 'Saving...' : 'Bad Lead - Did Not Want Quote'}
                      </button>
                    </div>
                    <button
                      onClick={() => setShowCompleteModal(false)}
                      className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center gap-3 rounded-b-lg">
              {canClaim && (
                <button
                  onClick={() => handleAction(() => onClaim(lead.leadId))}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md"
                >
                  {isLoading ? 'Claiming...' : 'Claim Lead'}
                </button>
              )}
              {canUnclaim && (
                <button
                  onClick={() => handleAction(() => onUnclaim(lead.leadId))}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 disabled:bg-gray-200 rounded-md"
                >
                  {isLoading ? 'Releasing...' : 'Release Lead'}
                </button>
              )}
              {canComplete && (
                <button
                  onClick={() => setShowCompleteModal(true)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-md"
                >
                  Mark Complete
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InsuranceLeadDetail;
